import React, { memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import ContributeFormNode from '../ContributeFormNode';
import arePropsEqual from '../../../../helpers/arePropsEqual';
import { addFocusOnFirstField } from '../../../../helpers/formTree';
import ComputedHeightsContext from '../../ContributeFormTemplate/ComputedHeightsContext';
import HiddenBoxesContext from '../../ContributeFormTemplate/HiddenBoxesContext';

// Root form node
const ContributeFormNodeRoot = memo(({
  node,
  readonly = false,
  answersValues,
  blocksValues,
  reviewTree,
  isReview,
}) => {
  const blocks = useContext(ComputedHeightsContext);
  const hiddenBoxes = useContext(HiddenBoxesContext);

  // Mark the first visible field with isFocusOnInit: true.
  // hiddenBoxes is pre-computed, so no DOM traversal needed.
  const childrenWithFocus = useMemo(
    () => readonly ? node.children : addFocusOnFirstField(node.children, hiddenBoxes),
    [node.children, hiddenBoxes, readonly],
  );

  // Get Y positions from precomputed heights (already reflowed)
  const childrenYPositions = useMemo(() => {
    const rootData = blocks.get('root');
    if (!rootData?.childrenBoxes?.[0]) return {};

    const positions = {};
    const reflowedBoxes = rootData.childrenBoxes[0];
    for (let i = 0; i < node.children.length; i++) {
      positions[i] = reflowedBoxes[i]?.y ?? node.children[i].box.y;
    }
    return positions;
  }, [blocks, node.children]);

  if (!Array.isArray(node?.children)) {
    return null;
  }

  return childrenWithFocus.map((childNode, i) => (
    <ContributeFormNode
      key={i}
      node={childNode}
      boxOverride={{ y: childrenYPositions[i] ?? childNode.box.y }}
      readonly={readonly}
      answersValues={answersValues}
      blocksValues={blocksValues}
      reviewTree={reviewTree}
      isReview={isReview}
    />
  ));
}, arePropsEqual);

ContributeFormNodeRoot.displayName = 'ContributeFormNodeRoot';

ContributeFormNodeRoot.propTypes = {
  node: PropTypes.shape({
    box: PropTypes.shape({
      hasHeader: PropTypes.bool,
      height: PropTypes.number,
      width: PropTypes.number,
      x: PropTypes.number,
      y: PropTypes.number,
    }),
    children: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        children: PropTypes.array,
        box: PropTypes.shape({
          height: PropTypes.number,
          width: PropTypes.number,
          x: PropTypes.number,
          y: PropTypes.number,
        }),
      }),
    ),
    duplicable: PropTypes.bool,
  }).isRequired,
  readonly: PropTypes.bool,
  answersValues: PropTypes.arrayOf(PropTypes.object),
  blocksValues: PropTypes.object,
  reviewTree: PropTypes.object,
  isReview: PropTypes.bool,
};

export default ContributeFormNodeRoot;
